/*
 * Copyright 2024 Adobe. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */

/* eslint-env browser */
function sampleRUM(checkpoint, data) {
  const timeShift = () => (window.performance ? window.performance.now() : Date.now() - window.hlx.rum.firstReadTime);
  try {
    window.hlx = window.hlx || {};
    sampleRUM.enhance = () => {};
    if (!window.hlx.rum) {
      const param = new URLSearchParams(window.location.search).get('rum');
      const weight = (window.SAMPLE_PAGEVIEWS_AT_RATE === 'high' && 10)
        || (window.SAMPLE_PAGEVIEWS_AT_RATE === 'low' && 1000)
        || (param === 'on' && 1)
        || 100;
      const id = Math.random().toString(36).slice(-4);
      const isSelected = param !== 'off' && Math.random() * weight < 1;

      window.hlx.rum = {
        weight,
        id,
        isSelected,
        firstReadTime: window.performance ? window.performance.timeOrigin : Date.now(),
        sampleRUM,
        queue: [],
        collector: (...args) => window.hlx.rum.queue.push(args),
      };
      if (isSelected) {
        const dataFromErrorObj = (error) => {
          const errData = { source: 'undefined error' };
          try {
            errData.target = error.toString();
            errData.source = error.stack
              .split('\n')
              .filter((line) => line.match(/https?:\/\//))
              .shift()
              .replace(/at ([^ ]+) \((.+)\)/, '$1@$2')
              .replace(/ at /, '@')
              .trim();
          } catch {
            /* error structure was not as expected */
          }
          return errData;
        };

        window.addEventListener('error', ({ error }) => {
          const errData = dataFromErrorObj(error);
          sampleRUM('error', errData);
        });

        window.addEventListener('unhandledrejection', ({ reason }) => {
          let errData = {
            source: 'Unhandled Rejection',
            target: reason || 'Unknown',
          };
          if (reason instanceof Error) {
            errData = dataFromErrorObj(reason);
          }
          sampleRUM('error', errData);
        });

        sampleRUM.baseURL = sampleRUM.baseURL || new URL(window.RUM_BASE || '/', new URL('https://rum.hlx.page'));
        sampleRUM.collectBaseURL = sampleRUM.collectBaseURL || sampleRUM.baseURL;
        sampleRUM.sendPing = (ck, time, pingData = {}) => {
          const rumData = JSON.stringify({
            weight,
            id,
            referer: window.location.href,
            checkpoint: ck,
            t: time,
            ...pingData,
          });
          const urlParams = window.RUM_PARAMS
            ? `?${new URLSearchParams(window.RUM_PARAMS).toString()}`
            : '';
          const { href: url, origin } = new URL(
            `.rum/${weight}${urlParams}`,
            sampleRUM.collectBaseURL,
          );
          const body = origin === window.location.origin
            ? new Blob([rumData], { type: 'application/json' })
            : rumData;
          navigator.sendBeacon(url, body);
          // eslint-disable-next-line no-console
          console.debug(`ping:${ck}`, pingData);
        };
        sampleRUM.sendPing('top', timeShift());

        sampleRUM.enhance = () => {
          // only enhance once
          if (document.querySelector('script[src*="rum-enhancer"]')) return;

          const script = document.createElement('script');
          script.src = new URL(
            '.rum/@adobe/helix-rum-enhancer@^2/src/index.js',
            sampleRUM.baseURL,
          ).href;
          document.head.appendChild(script);
        };
        if (!window.hlx.RUM_MANUAL_ENHANCE) {
          sampleRUM.enhance();
        }
      }
    }
    if (window.hlx.rum && window.hlx.rum.isSelected && checkpoint) {
      window.hlx.rum.collector(checkpoint, data, timeShift());
    }
    document.dispatchEvent(new CustomEvent('rum', { detail: { checkpoint, data } }));
  } catch {
    // something went awry
  }
}

/**
 * Setup block utils.
 */
function setup() {
  window.hlx = window.hlx || {};
  window.hlx.RUM_MASK_URL = 'full';
  window.hlx.RUM_MANUAL_ENHANCE = true;
  window.hlx.codeBasePath = '';
  window.hlx.lighthouse = new URLSearchParams(window.location.search).get('lighthouse') === 'on';

  const scriptEl = document.querySelector('script[src$="/dist/scripts/scripts.js"]');
  if (scriptEl) {
    try {
      [window.hlx.codeBasePath] = new URL(scriptEl.src).pathname.split('/dist/scripts/scripts.js');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
    }
  }
}

/**
 * Auto initializiation.
 */

function init() {
  setup();
  sampleRUM();
}

/**
 * Sanitizes a string for use as class name.
 * @param {string} name The unsanitized string
 * @returns {string} The class name
 */
function toClassName(name) {
  return typeof name === 'string'
    ? name
        .toLowerCase()
        .replace(/[^0-9a-z]/gi, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '')
    : '';
}

/**
 * @param {string} str The input string
 * @returns {string} The capitalized string
 */
function capitalize(str) {
  if (!str || str.length === 0) return '';
  const lower = str.toLowerCase();
  return lower.substring(0, 1).toUpperCase() + lower.substring(1, lower.length);
}

/**
 * Sanitizes a string for use as a js property name.
 * @param {string} name The unsanitized string
 * @returns {string} The camelCased name
 */
function toCamelCase(name) {
  const parts = name
    ?.replace(/([A-Z])+/g, capitalize)
    .replace(/[^0-9a-zA-Z]/gi, '-')
    // eslint-disable-next-line no-useless-escape
    ?.split(/(?=[A-Z])|[\.\-\s_]/)
    .map((x) => x.toLowerCase()) ?? [];
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0];
  return parts.reduce((acc, part) => {
    return `${acc}${part.charAt(0).toUpperCase()}${part.slice(1)}`;
  });
}

/**
 * Extracts the config from a block.
 * @param {Element} block The block element
 * @returns {object} The block config
 */

function readBlockConfig(block) {
  const config = {};
  block.querySelectorAll(':scope > div').forEach((row) => {
    if (row.children) {
      const cols = [...row.children];
      if (cols[1]) {
        const col = cols[1];
        const name = toClassName(cols[0].textContent);
        let value = '';
        if (col.querySelector('a')) {
          const as = [...col.querySelectorAll('a')];
          if (as.length === 1) {
            value = as[0].href;
          } else {
            value = as.map((a) => a.href);
          }
        } else if (col.querySelector('img')) {
          const imgs = [...col.querySelectorAll('img')];
          if (imgs.length === 1) {
            value = imgs[0].src;
          } else {
            value = imgs.map((img) => img.src);
          }
        } else if (col.querySelector('p')) {
          const ps = [...col.querySelectorAll('p')];
          if (ps.length === 1) {
            value = ps[0].textContent;
          } else {
            value = ps.map((p) => p.textContent);
          }
        } else value = row.children[1].textContent;
        config[name] = value;
      }
    }
  });
  return config;
}

/**
 * Loads a CSS file.
 * @param {string} href URL to the CSS file
 */
async function loadCSS(href) {
  return new Promise((resolve, reject) => {
    if (!document.querySelector(`head > link[href="${href}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      link.onload = resolve;
      link.onerror = reject;
      document.head.append(link);
    } else {
      resolve();
    }
  });
}

/**
 * Loads a non module JS file.
 * @param {string} src URL to the JS file
 * @param {Object} attrs additional optional attributes
 */
async function loadScript(src, attrs) {
  return new Promise((resolve, reject) => {
    if (!document.querySelector(`head > script[src="${src}"]`)) {
      const script = document.createElement('script');
      script.src = src;
      if (attrs) {
        for (const attr in attrs) {
          script.setAttribute(attr, attrs[attr]);
        }
      }
      script.onload = resolve;
      script.onerror = reject;
      document.head.append(script);
    } else {
      resolve();
    }
  });
}

/**
 * Retrieves the content of metadata tags.
 * @param {string} name The metadata name (or property)
 * @param {Document} doc Document object to query for metadata. Defaults to the window's document
 * @returns {string} The metadata value(s)
 */
function getMetadata(name, doc = document) {
  const attr = name && name.includes(':') ? 'property' : 'name';
  const meta = [...doc.head.querySelectorAll(`meta[${attr}="${name}"]`)]
    .map((m) => m.content)
    .join(', ');
  return meta || '';
}

/**
 * Returns a picture element with webp and fallbacks
 * @param {string} src The image URL
 * @param {string} [alt] The image alternative text
 * @param {boolean} [eager] Set loading attribute to eager
 * @param {Array} [breakpoints] Breakpoints and corresponding params (eg. width)
 * @returns {Element} The picture element
 */
function createOptimizedPicture(
  src,
  alt = '',
  eager = false,
  breakpoints = [{ media: '(min-width: 600px)', width: '2000' }, { width: '750' }],
) {
  const url = new URL(src, window.location.href === 'about:srcdoc' ? document.baseURI : window.location.href);
  const picture = document.createElement('picture');
  const { pathname } = url;
  const ext = pathname.substring(pathname.lastIndexOf('.') + 1);

  // webp
  breakpoints.forEach((br) => {
    const source = document.createElement('source');
    if (br.media) source.setAttribute('media', br.media);
    source.setAttribute('type', 'image/webp');
    source.setAttribute('srcset', `${pathname}?width=${br.width}&format=webply&optimize=medium`);
    picture.appendChild(source);
  });

  // fallback
  breakpoints.forEach((br, i) => {
    if (i < breakpoints.length - 1) {
      const source = document.createElement('source');
      if (br.media) source.setAttribute('media', br.media);
      source.setAttribute('srcset', `${pathname}?width=${br.width}&format=${ext}&optimize=medium`);
      picture.appendChild(source);
    } else {
      const img = document.createElement('img');
      img.setAttribute('loading', eager ? 'eager' : 'lazy');
      img.setAttribute('alt', alt);
      picture.appendChild(img);
      img.setAttribute('src', `${pathname}?width=${br.width}&format=${ext}&optimize=medium`);
    }
  });

  return picture;
}

/**
 * Creates an SVG image element.
 * @param {string} src - The source URL of the SVG image.
 * @param {string} [alt=''] - The alternative text for the SVG image.
 * @returns {HTMLImageElement} - The created SVG image element.
 */
function createSVGImage(src, alt = '') {
  const img = document.createElement('img');
  img.src = src;
  img.alt = alt;
  img.loading = 'lazy';
  return img;
}

/**
 * Set template (page structure) and theme (page styles).
 */
function decorateTemplateAndTheme() {
  const addClasses = (element, classes) => {
    classes.split(',').forEach((c) => {
      element.classList.add(toClassName(c.trim()));
    });
  };
  const template = getMetadata('template');
  if (template) addClasses(document.body, template);
  const theme = getMetadata('theme');
  if (theme) addClasses(document.body, theme);
}

/**
 * Add <img> for icon, prefixed with codeBasePath and optional prefix.
 * @param {Element} [span] span element with icon classes
 * @param {string} [prefix] prefix to be added to icon src
 * @param {string} [alt] alt text to be added to icon
 */
function decorateIcon(span, prefix = '', alt = '') {
  const iconName = Array.from(span.classList)
    .find((c) => c.startsWith('icon-'))
    .substring(5);
  const img = document.createElement('img');
  img.dataset.iconName = iconName;
  img.src = `${window.hlx.codeBasePath}${prefix}/assets/icons/${iconName}.svg`;
  img.alt = alt;
  img.loading = 'lazy';
  span.append(img);
}

/**
 * Add <img> for icons, prefixed with codeBasePath and optional prefix.
 * @param {Element} [element] Element containing icons
 * @param {string} [prefix] prefix to be added to icon the src
 */
function decorateIcons(element, prefix = '') {
  const nonIconElements = 'icon-label-';
  const icons = [...element.querySelectorAll('span.icon:not(.svg-icon)')].filter((icon) => ![...icon.classList].some((c) => c.startsWith(nonIconElements)));
  icons.forEach((span) => {
    decorateIcon(span, prefix);
  });
}

const ICONS_CACHE = {};
/**
 * Replace icons with inline SVG and prefix with codeBasePath.
 * @param {Element} element
 */
async function decorateIconsToSVG(element = document) {
  // Prepare the inline sprite
  let svgSprite = document.getElementById('svg-sprite');
  if (!svgSprite) {
    const div = document.createElement('div');
    div.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" id="svg-sprite" style="display: none"></svg>';
    svgSprite = div.firstElementChild;
    document.body.prepend(svgSprite);
  }

  // Download new icons
  const allIcons = [...element.querySelectorAll('span.icon')];
  const nonIconElements = 'icon-label-';
  const icons = allIcons.filter((icon) => ![...icon.classList].some((c) => c.startsWith(nonIconElements)));

  await Promise.all([...icons].map(async (span) => {
    const iconName = Array.from(span.classList).find((c) => c.startsWith('icon-')).substring(5);

    if (!ICONS_CACHE[iconName]) {
      ICONS_CACHE[iconName] = true;
      try {
        const response = await fetch(`${window.hlx.codeBasePath}/assets/icons/${iconName}.svg`);
        if (!response.ok) {
          console.warn(`Failed to load icon ${iconName}`);
          ICONS_CACHE[iconName] = false;
          return;
        }
        // Styled icons don't play nice with the sprite approach because of shadow dom isolation
        const svg = await response.text();
        if (svg.match(/(<style | class=)/)) {
          ICONS_CACHE[iconName] = { styled: true, html: svg };
        } else {
          ICONS_CACHE[iconName] = {
            html: svg
              .replace('<?xml version="1.0" encoding="UTF-8"?>', '')
              .replace('<svg', `<symbol id="icons-sprite-${iconName}"`)
              .replace(/ width=".*?"/, '')
              .replace(/ height=".*?"/, '')
              // TODO: replace color with currentcolor?
              .replace('</svg>', '</symbol>'),
          };
        }
      } catch (error) {
        ICONS_CACHE[iconName] = false;
        console.error(error);
      }
    }
  }));

  // Add icons to the sprite
  const symbols = Object.entries(ICONS_CACHE)
    .filter(([iconName, { styled }]) => !(svgSprite.querySelector(`#icons-sprite-${iconName}`) || styled))
    .map(([, { html }]) => html)
    .join('\n');
  svgSprite.innerHTML += symbols;

  // Decorate the icons in the page using the symbol
  icons.forEach((span) => {
    span.classList.add('svg-icon'); // icons decorated here are to be omitted by the default icon loader
    const iconName = Array.from(span.classList).find((c) => c.startsWith('icon-')).substring(5);
    // Styled icons need to be inlined as-is, while unstyled ones can leverage the sprite
    if (ICONS_CACHE[iconName].styled) {
      span.append(ICONS_CACHE[iconName].html);
    } else {
      span.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><use href="#icons-sprite-${iconName}"/></svg>`;
    }
  });
}

/**
 * Wrap inline text content of block cells within a <p> tag.
 * @param {Element} block the block element
 */
function wrapTextNodes(block) {
  const validWrappers = [
    'P',
    'PRE',
    'UL',
    'OL',
    'PICTURE',
    'TABLE',
    'H1',
    'H2',
    'H3',
    'H4',
    'H5',
    'H6',
  ];

  const wrap = (el) => {
    const wrapper = document.createElement('p');
    wrapper.append(...el.childNodes);
    el.append(wrapper);
  };

  block.querySelectorAll(':scope > div > div').forEach((blockColumn) => {
    if (blockColumn.hasChildNodes()) {
      const hasWrapper = !!blockColumn.firstElementChild
        && validWrappers.some((tagName) => blockColumn.firstElementChild.tagName === tagName);
      if (!hasWrapper) {
        wrap(blockColumn);
      } else if (
        blockColumn.firstElementChild.tagName === 'PICTURE'
        && (blockColumn.children.length > 1 || !!blockColumn.textContent.trim())
      ) {
        wrap(blockColumn);
      }
    }
  });
}

/**
 * Retrieves a placeholder text by its label.
 * @param {string} label - The label of the placeholder to retrieve.
 * @param {string} [prefix='default'] - The prefix for the placeholder location.
 * @param {boolean} [fallbackLabel=true] - Fall back to label (true) or empty string (false)
 * in case of falsy placeholder
 * @returns {string} - The placeholder text corresponding to the label.
 */
function getPlaceholder(label, prefix = 'default', fallbackLabel = true) {
  return window?.placeholders?.[prefix]?.[label] || (fallbackLabel ? label : '');
}

/**
 * Retrieves a taxonomy placeholder text by its label.
 * If the label starts with 'key:', it attempts to fetch the corresponding taxonomy placeholder.
 * In case when the taxonomy placeholder is not found, it returns the label without the 'key:' prefix.
 * Otherwise, it returns the label without modification.
 *
 * @param {string} label - The label of the taxonomy placeholder to retrieve.
 * @returns {string} - The taxonomy placeholder text corresponding to the label,
 * or the label itself if no placeholder is found.
 */
function getTaxPlaceholder(label) {
  if (label.startsWith('key:')) {
    return window?.placeholders?.taxonomy?.[label] || label.substring(4);
  }

  return label;
}

/**
 * Checks if a given string is a valid URL.
 * @param {string} string - The string to check.
 * @returns {URL} - Returns true if the string is a valid URL, otherwise false.
 */
function isValidUrl(string) {
  try {
    return new URL(string);
  } catch {
    return false;
  }
}

/**
 * Gets the extension of a URL.
 * @param {string} url The URL
 * @returns {string} The extension
 * @private
 * @example
 * get_url_extension('https://example.com/foo.jpg');
 * // returns 'jpg'
 * get_url_extension('https://example.com/foo.jpg?bar=baz');
 * // returns 'jpg'
 * get_url_extension('https://example.com/foo');
 * // returns ''
 * get_url_extension('https://example.com/foo.jpg#qux');
 * // returns 'jpg'
 */
function getUrlExtension(url) {
  const extension = url.split(/[#?]/)[0].split('.').pop().trim();
  return extension && !extension.includes('/') ? extension : '';
}

/**
 * Checks if a URL has an extension that is included in the provided array of extensions.
 * @param {string} url - The URL to check.
 * @param {Array<string>} extensionsArray - An array of valid extensions to check against.
 * @returns {boolean} - Returns true if the URL has a valid extension, otherwise false.
 */
function checkAssetExtension(url, extensionsArray) {
  const extension = getUrlExtension(url);
  return extension && extensionsArray.includes(extension.toLowerCase());
}

/**
  * Appends query params to a URL
  * @param {string} url The URL to append query params to
  * @param {object} params The query params to append
  * @returns {string} The URL with query params appended
  * @private
  * @example
  * appendQueryParams('https://example.com', { foo: 'bar' });
  * // returns 'https://example.com?foo=bar'
*/
function appendQueryParams(url, params) {
  const { searchParams } = url;
  params.forEach((value, key) => {
    searchParams.set(key, value);
  });
  url.search = searchParams.toString();
  return url.toString();
}

/**
 * Adds Labels component to a given container
 * @param {array} data An array with the labels, categories and icon visibility
 *                e.g. [{ label: 'category', value: 'Insights', hideIcon: false }]
 */

function createLabels(data, options = {}) {
  const { variant } = options;
  const labelsContainer = Object.assign(document.createElement('div'), {
    className: `labels-container ${variant || ''}`,
  });

  // list of labels types in which an icon should be added, and the icon name
  const labelTypes = {
    date: 'calendar',
    time: 'clock',
    language: 'globe',
  };

  data.forEach((item) => {
    if (!item.label) {
      return;
    }

    const label = Object.assign(document.createElement('div'), {
      className: 'label',
    });

    label.append(Object.assign(document.createElement('span'), {
      innerHTML: getTaxPlaceholder(item.label),
    }));

    if (labelTypes[item.type] && !item.hideIcon) {
      label.prepend(Object.assign(document.createElement('span'), {
        className: `icon icon-icon-${labelTypes[item.type]}`,
      }));
    }
    labelsContainer.append(label);
  });

  decorateIconsToSVG(labelsContainer);

  return labelsContainer;
}

/**
 * Retrieves the alternative text for an image element.
 * @param {Element} imageLink - The anchor element to get the alternative text for.
 * @returns {string} - The alternative text for the image.
 */
function getAltText(imageLink) {
  const href = imageLink.getAttribute('href');
  const textContent = imageLink.textContent.trim();
  const textUrl = isValidUrl(textContent);

  if (textUrl || href.includes(textContent)) {
    return '';
  }

  return textContent;
}

/**
 * Creates an optimized picture element from a link.
 * @param {string} assetSrc - The source URL of the asset.
 * @param {string} altText - The alternative text for the image.
 * @param {boolean} eager - Whether to load the image eagerly.
 * @returns {HTMLPictureElement} - The created picture element.
 */
function createPictureFromLink(assetSrc, altText, eager) {
  const extPicture = createOptimizedPicture(assetSrc, altText, eager);
  /* copy query params from link to img */
  const assetUrl = isValidUrl(assetSrc);

  if (assetUrl) {
    const { searchParams } = assetUrl;
    extPicture.querySelectorAll('source, img').forEach((child) => {
      if (child.tagName === 'SOURCE') {
        const srcset = child.getAttribute('srcset');
        if (srcset) {
          child.setAttribute('srcset', appendQueryParams(new URL(srcset, assetSrc), searchParams));
        }
      } else if (child.tagName === 'IMG') {
        const src = child.getAttribute('src');
        if (src) {
          child.setAttribute('src', appendQueryParams(new URL(src, assetSrc), searchParams));
        }
      }
    });
  }
  return extPicture;
}

/**
 * Creates a video element with a poster image
 * that will exchange for the video tag once loaded
 * @param {Element} posterImage - The poster image element.
 * @param {string} videoSrc - The source URL of the video asset.
 * @param videoType - The type of video element to create: 'HTML5' or 'Vidyard'.
 * @returns {Element} - The created video element.
 */
function createAutoplayVideo(posterImage, videoSrc, videoType = 'html5', isInBlock) {
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // only .video-wrap-autoplay class is added,
  // regular .video-wrapp will be added if modal is needed
  const wrapper = Object.assign(
    document.createElement('div'),
    { className: 'video-wrap-autoplay' },
  );

  const playerWrapper = Object.assign(
    document.createElement('div'),
    { className: 'auto-player-wrapper' },
  );

  const em = posterImage.parentNode;
  wrapper.append(posterImage, playerWrapper);
  if (!isInBlock) {
    em.remove();
  }
  if (videoType === 'html5' && !prefersReducedMotion) {
    const video = Object.assign(
      document.createElement('video'),
      {
        src: videoSrc,
        autoplay: true,
        muted: true,
        playsinline: true,
        loop: true,
        style: 'width: 100%; height: 100%; display: none;',
      },
    );
    playerWrapper.append(video);

    video.addEventListener('loadeddata', () => {
      video.style.display = 'block';
      posterImage.classList.add('visually-hidden');
    });
  }
  return wrapper;
}

function addVideoModal(wrapper, videoSrc, videoType) {
  wrapper.classList.add('video-wrap');
  const button = Object.assign(
    document.createElement('button'),
    { className: 'open-button' },
  );
  button.innerHTML = `<span class="visually-hidden">${getPlaceholder('playVideo')}</span>`;

  const dialog = Object.assign(
    document.createElement('dialog'),
    { className: `video-dialog dark-base-theme ${videoType}` },
  );
  dialog.innerHTML = `
    <button class="close-button">
      <span class="icon icon-close"></span><span class="visually-hidden">${getPlaceholder('close')}</span>
    </button>
    <div class="player-container">
      <div class="player-wrapper"></div>
    </div>
  `;
  wrapper.prepend(button);
  wrapper.append(dialog);
  const playerWrapper = dialog.querySelector('.player-wrapper');

  if (videoType === 'html5') {
    playerWrapper.innerHTML = `
      <video data-src="${videoSrc}" preload="none" controls playsinline></video>
    `;
  } else if (videoType === 'vidyard') {
    const url = new URL(videoSrc);
    const videoId = url.pathname.split('/').pop();

    playerWrapper.innerHTML = `
      <img src="${videoSrc}" class="vidyard-player-embed" data-uuid="${videoId}" data-v="4" data-type="inline" />
  `;
  }
  return wrapper;
}

/**
 * Creates a poster Image that opens video in a modal
 * @param {Element} posterImage - The poster image element to click on
 * @param {Element} assetLink - The anchor element containing the asset link.
 * @param {string} assetSrc - The source URL of the video asset.
 * @param videoType - The type of video element to create: 'HTML5' or 'Vidyard'.
 * @returns {Element} - The created video element.
 */

function createVideoModal(posterImage, assetLink, assetSrc, videoType) {
  let wrapper = document.createElement('div');
  wrapper.className = 'video-wrap';
  wrapper.append(posterImage);
  wrapper.append(Object.assign(
    document.createElement('div'),
    {
      className: 'play-sign',
      innerHTML: '<span class="icon icon-play"></span>',
    },
  ));
  wrapper = addVideoModal(wrapper, assetSrc, videoType);
  assetLink.before(wrapper);
  decorateIconsToSVG(wrapper);
  return wrapper;
};

/**
 * Handles a video element from a given asset link and source.
 * It will create a poster image or video element with the proper
 * type and behaviour: autoplay / modal / both
 * @param {Element} assetLink - The anchor element containing the asset link.
 * @param {string} assetSrc - The source URL of the video asset.
 * @param videoType - The type of video element to create: 'HTML5' or 'Vidyard'.
 * @returns {Element} - The created video element.
 */
function handleVideo(assetLink, assetSrc, videoType = 'html5') {
  const parent = assetLink.parentNode;
  const autoplayVideo = parent.tagName === 'EM'; // autoplay videos are inside emphasis tags

  if (autoplayVideo) {
    const previousAutoplayVideo = parent.parentNode.querySelector('.video-wrap-autoplay');
    // if parent has an autoplay video this assetLink video
    // should be opened in a modal after click previous
    if (previousAutoplayVideo) {
      const wrapper = addVideoModal(previousAutoplayVideo, assetSrc, videoType);
      decorateIconsToSVG(wrapper);
      return '';
    }

    const autoplayPosterImage = parent.parentNode.querySelector('picture');
    if (autoplayPosterImage) {
      let isInBlock = false;
      if (parent.querySelector('picture')) {
        isInBlock = true;
      }
      const wrapper = createAutoplayVideo(autoplayPosterImage, assetSrc, videoType, isInBlock);
      assetLink.before(wrapper);
      return wrapper;
    }
  } else {
    const posterImage = parent.querySelector('picture');
    if (posterImage) {
      return createVideoModal(posterImage, assetLink, assetSrc, videoType);
    }
  }
  return assetLink;
}

function setLazyVideosObserver(videoSrc) {
  const observer = new IntersectionObserver((entries, observer) => {
    entries.forEach((entry) => {
      const video = entry.target;

      if (entry.isIntersecting) {
        video.play().catch((err) => {
          console.error('Failed to autoplay video:', err);
        });

        // Unobserve if you only want it to start once
        observer.unobserve(video);
      }
    });
  }, {
    threshold: 0.2,
  });

  observer.observe(videoSrc);
}

function lazyLoadVideos(autoPlayVideos) {
  autoPlayVideos.forEach((video) => {
    const videoSrc = video && video.querySelector('video');
    if (videoSrc) {
      videoSrc.pause(); // Pause video initially
      videoSrc.setAttribute('preload', 'none');
      videoSrc.setAttribute('muted', '');
      videoSrc.setAttribute('playsinline', '');
      videoSrc.setAttribute('autoplay', '');

      setLazyVideosObserver(videoSrc);
    }
  });
}

/**
 * Checks if the current host is a development host. True when inside an iframe in sidekick.
 * @returns {boolean} - Returns true if the current host is a development host, otherwise false.
 */
const isDevHost = () => {
  if (window.location.ancestorOrigins?.length) {
    return true;
  }

  const devHosts = ['localhost', '127.0.0.1', 'aem.page', 'aem.live', 'staging-commercial'];
  return devHosts.some((url) => window.location.host.includes(url));
};

/**
 * Sets the ID of a block element based on its class.
 * Uses in block can be: (id: my-block-id), (id my-block-id) or (id-my-block-id)
 * @param {HTMLElement} block
 */
function decorateId(block) {
  const idClass = [...block.classList].find((c) => c.startsWith('id-'));
  if (idClass) {
    const id = idClass.substring(3);
    block.id = id;
  }
}

/**
 * Wraps the given element in an anchor (`<a>`) tag if an `href` is provided.
 * Useful for conditionally making elements (like images or picture elements) clickable.
 *
 * @param {HTMLElement} element - The DOM element to wrap.
 * @param {object} options - Configuration options for the anchor.
 * @param {string} options.href - The URL to use in the anchor's `href` attribute. If not provided, the original element is returned.
 * @param {string} [options.alt] - Optional value for the anchor's `title` attribute (e.g., for accessibility or tooltips).
 * @param {boolean} [options.openInNewTab] - If true, sets `target="_blank"` to open the link in a new tab and adds `rel="noopener noreferrer"` for security.
 * @returns {HTMLElement} - The original element or the element wrapped in an anchor.
 */
function wrapElementWithAnchor(element, options) {
  if (!element) return;
  if (!options?.href) return element;

  const wrapperAnchor = document.createElement('a');
  wrapperAnchor.setAttribute('href', options?.href);
  wrapperAnchor.setAttribute('title', options?.alt || '');

  if (options?.openInNewTab) {
    wrapperAnchor.setAttribute('target', '_blank');
    wrapperAnchor.setAttribute('rel', 'noopener noreferrer');
  }

  wrapperAnchor.append(element);
  return wrapperAnchor;
}

/**
  * Decorates assets with corresponding element
  * @param {Element} element The element
  */
function decorateAssets(element) {
  const assetLinks = element.querySelectorAll('a:not(.button-container a)');

  const autoPlayVideos = [];
  assetLinks.forEach((assetLink) => {
    const assetSrc = assetLink.getAttribute('href');
    const altText = getAltText(assetLink);
    if (getUrlExtension(assetSrc)) {
      if (checkAssetExtension(assetSrc, ['jpg', 'jpeg', 'png', 'gif', 'webp'])) {
        const parentElement = assetLink.parentNode;
        const loadImageEager = parentElement.closest('.load-image-eager');

        const extPicture = createPictureFromLink(assetSrc, altText, loadImageEager);

        parentElement.replaceChild(extPicture, assetLink);

        const imageLink = parentElement.querySelector('a');
        const imageLinkHref = imageLink?.getAttribute('href') || '';

        const isEligibleForWrap
          = imageLink
            && imageLinkHref
            && parentElement.tagName === 'P'
            && !checkAssetExtension(imageLinkHref, ['mp4', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'])
            && !imageLinkHref.includes('videos.celonis');

        if (isEligibleForWrap) {
          const imageWithLink = wrapElementWithAnchor(extPicture, {
            href: imageLinkHref,
            alt: imageLink?.innerText?.trim(),
            openInNewTab: true,
          });

          parentElement.replaceChildren(imageWithLink);
        }
      }

      if (checkAssetExtension(assetSrc, ['svg'])) {
        const imageSVG = createSVGImage(assetSrc, altText);
        assetLink.replaceWith(imageSVG);
      }

      if (checkAssetExtension(assetSrc, ['mp4'])) {
        const videoHTML = handleVideo(assetLink, assetSrc);

        // Autoplay videos are inside emphasis tags
        if (assetLink.parentNode.tagName === 'EM') {
          assetLink.parentNode.replaceWith(videoHTML);
          autoPlayVideos.push(videoHTML);
        } else {
          assetLink.replaceWith(videoHTML);
        }
      }
    } else if (assetSrc.includes('videos.celonis') && encodeURI(assetLink.textContent.trim()).indexOf(assetSrc) !== -1) {
      const videoVidyard = handleVideo(assetLink, assetSrc, 'vidyard');
      assetLink.replaceWith(videoVidyard);
    } else if (assetSrc.includes('photos')) {
      const extPicture = createPictureFromLink(assetSrc, altText);
      assetLink.parentNode.replaceChild(extPicture, assetLink);
    }
  });

  if (autoPlayVideos.length > 0) {
    lazyLoadVideos(autoPlayVideos);
  }
}

/**
 * Decorates paragraphs containing a single link as buttons.
 * @param {Element} element container element
 */
function decorateButtons(element) {
  element.querySelectorAll('a').forEach((a) => {
    a.title = a.title || a.textContent.trim();
    a.setAttribute('role', 'button');
    if (a.href !== a.textContent) {
      const up = a.parentElement;
      const twoup = a.parentElement.parentElement;
      if (!a.querySelector('img')) {
        if (up.childNodes.length === 1 && (up.tagName === 'P' || up.tagName === 'DIV')) {
          a.classList.add('button'); // default
          up.classList.add('button-container');
        }
        if (
          up.childNodes.length === 1
          && up.tagName === 'STRONG'
          && twoup.childNodes.length === 1
          && twoup.tagName === 'P'
        ) {
          a.className = 'button primary';
          twoup.classList.add('button-container');
        }
        if (
          up.childNodes.length === 1
          && up.tagName === 'EM'
          && twoup.childNodes.length === 1
          && twoup.tagName === 'P'
        ) {
          a.className = 'button secondary';
          twoup.classList.add('button-container');
        }
        if (
          up.childNodes.length === 1
          && up.tagName === 'EM'
          && twoup.childNodes.length === 1
          && twoup.tagName === 'STRONG'
        ) {
          a.className = 'button conversion';
          twoup.parentElement.classList.add('button-container');
        }
        if (a.childNodes.length > 0 && a.childNodes[0].nodeType === Node.TEXT_NODE) {
          const span = document.createElement('span');
          span.append(a.childNodes[0]);
          a.prepend(span);
        }
      }
      const regexIcon = /#icon-([\w-]+)/;
      const iconMatch = a.href.match(regexIcon);
      if (iconMatch && iconMatch.length > 1 && !a.querySelector('.icon')) {
        const icon = iconMatch[1];
        a.append(Object.assign(document.createElement('span'), {
          className: `icon icon-icon-${icon}`,
        }));
        a.href = a.href.replace(regexIcon, '');
      }
      if (a.querySelector('.icon')) {
        decorateIconsToSVG(a);
      }
      // See if a tag has no text, only an icon
      if ((!a.textContent.trim() || a?.firstChild.classList.contains('visually-hidden')) && !a.querySelector('picture')) {
        a.classList.add('icon-button');
        a.firstChild?.setAttribute('aria-hidden', 'true');
        a.firstChild?.setAttribute('focusable', 'false');
      }
    }
  });
}

/**
 * Decorates paragraphs containing a single link as buttons.
 * @param {Element} element container element
 */
function decorateIconButton(element) {
  element.querySelectorAll('.icon-only').forEach((icon) => {
    decorateIconsToSVG(icon);
  });
}

/**
 * Decorates all sections in a container element.
 * @param {Element} main The container element
 */
function decorateSections(main) {
  main.querySelectorAll(':scope > div').forEach((section) => {
    const wrappers = [];
    let defaultContent = false;
    [...section.children].forEach((e) => {
      if (e.tagName === 'DIV' || !defaultContent) {
        const wrapper = document.createElement('div');
        wrappers.push(wrapper);
        defaultContent = e.tagName !== 'DIV';
        if (defaultContent) wrapper.classList.add('default-content-wrapper');
      }
      wrappers[wrappers.length - 1].append(e);
    });
    wrappers.forEach((wrapper) => section.append(wrapper));
    section.classList.add('section');
    section.dataset.sectionStatus = 'initialized';
    section.style.display = 'none';

    // Process section metadata
    const sectionMeta = section.querySelector('div.section-metadata');
    if (sectionMeta) {
      const meta = readBlockConfig(sectionMeta);
      Object.keys(meta).forEach((key) => {
        if (key === 'style') {
          const styles = meta.style
            .split(',')
            .filter((style) => style)
            .map((style) => toClassName(style.trim()));
          styles.forEach((style) => section.classList.add(style));
        } else {
          section.dataset[toCamelCase(key)] = meta[key];
        }
      });
      sectionMeta.parentNode.remove();
    }
  });
}

async function fetchPlaceholders(prefix = 'default', language = '') {
  let currentLang = window.location.pathname.split('/')[1]?.toString().toLowerCase() || '';
  if (currentLang && currentLang.length !== 2) {
    currentLang = '';
  }
  if (language !== '') {
    language = `/${language}`;
  }

  window.placeholders = window.placeholders || {};
  window.placeholders[prefix] = new Promise((resolve) => {
    fetch(`${prefix === 'default' ? '' : prefix}${language}/placeholders.json`)
      .then((resp) => {
        if (resp.ok) {
          return resp.json();
        }
        return {};
      })
      .then((json) => {
        const placeholders = { default: {} };
        json.data
          .filter((placeholder) => placeholder.Key)
          .forEach((placeholder) => {
            if (placeholder.Key === 'default') {
              console.warn('The `default` value is reserved for the default placeholders, please use another key.');
              return;
            }
            placeholders[toCamelCase(placeholder.Key)] = placeholder[currentLang.toUpperCase()] || placeholder.Text;
            placeholders.default[toCamelCase(placeholder.Key)] = placeholder.Text;
          });

        window.placeholders[prefix] = placeholders;
        resolve(window.placeholders[prefix]);
      })
      .catch(() => {
        // error loading placeholders
        window.placeholders[prefix] = {};
        resolve(window.placeholders[prefix]);
      });
  });

  return window.placeholders[`${prefix}`];
}

/**
 * Builds a block DOM Element from a two dimensional array, string, or object
 * @param {string} blockName name of the block
 * @param {*} content two dimensional array or string or object of content
 */
function buildBlock(blockName, content) {
  const table = Array.isArray(content) ? content : [[content]];
  const blockEl = document.createElement('div');
  // build image block nested div structure
  blockEl.classList.add(blockName);
  table.forEach((row) => {
    const rowEl = document.createElement('div');
    row.forEach((col) => {
      const colEl = document.createElement('div');
      const vals = col.elems ? col.elems : [col];
      vals.forEach((val) => {
        if (val) {
          if (typeof val === 'string') {
            colEl.innerHTML += val;
          } else {
            colEl.appendChild(val);
          }
        }
      });
      rowEl.appendChild(colEl);
    });
    blockEl.appendChild(rowEl);
  });
  return blockEl;
}

/**
 * Loads JS and CSS for a block.
 * @param {Element} block The block element
 */
async function loadBlock(block) {
  const status = block.dataset.blockStatus;
  if (status !== 'loading' && status !== 'loaded') {
    block.dataset.blockStatus = 'loading';
    const { blockName } = block.dataset;
    try {
      const cssLoaded = loadCSS(`${window.hlx.codeBasePath}/dist/blocks/${blockName}/${blockName}.css`);
      const decorationComplete = new Promise((resolve) => {
        (async () => {
          try {
            const mod = await import(`${window.hlx.codeBasePath}/dist/blocks/${blockName}/${blockName}.js`);
            if (mod.default) {
              await mod.default(block);
            }
          } catch (error) {
            window.blockErrors.push(blockName);
            const blockErrorEvent = new CustomEvent('blockError', {
              detail: {
                blockName,
              },
            });
            document.dispatchEvent(blockErrorEvent);
            if (isDevHost()) {
              block.classList.add('block-error');
            } else {
              block.setAttribute('hidden', 'has-error');
            }
            // eslint-disable-next-line no-console
            console.log(`failed to load module for ${blockName}`, error);
          }
          resolve();
        })();
      });
      await Promise.all([cssLoaded, decorationComplete]);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(`failed to load block ${blockName}`, error);
    }
    block.dataset.blockStatus = 'loaded';
  }
  return block;
}

/**
 * Decorates a block.
 * @param {Element} block The block element
 */
function decorateBlock(block) {
  const shortBlockName = block.classList[0];
  if (shortBlockName) {
    block.classList.add('block');
    block.dataset.blockName = shortBlockName;
    if (!block.dataset.blockStatus) block.dataset.blockStatus = 'initialized';
    wrapTextNodes(block);
    const blockWrapper = block.parentElement;
    blockWrapper.classList.add(`${shortBlockName}-wrapper`);
    const section = block.closest('.section');
    if (section) section.classList.add(`${shortBlockName}-container`);
  }
}

/**
 * Decorates all blocks in a container element.
 * @param {Element} main The container element
 */
function decorateBlocks(main) {
  main.querySelectorAll('div.section > div > div').forEach((block) => {
    decorateId(block);
    decorateBlock(block);
  });
}

/**
 * Loads a block named 'header' into header
 * @param {Element} header header element
 * @returns {Promise}
 */
async function loadHeader(header) {
  const headerBlock = buildBlock('header', '');
  header.append(headerBlock);
  decorateBlock(headerBlock);
  return loadBlock(headerBlock);
}

/**
 * Loads a block named 'footer' into footer
 * @param footer footer element
 * @returns {Promise}
 */
async function loadFooter(footer) {
  const footerBlock = buildBlock('footer', '');
  footer.append(footerBlock);
  decorateBlock(footerBlock);
  return loadBlock(footerBlock);
}

/**
 * Wait for Image.
 * @param {Element} section section element
 */
async function waitForFirstImage(section) {
  const lcpCandidate = section.querySelector('img');
  await new Promise((resolve) => {
    if (lcpCandidate && !lcpCandidate.complete) {
      lcpCandidate.setAttribute('loading', 'eager');
      lcpCandidate.addEventListener('load', resolve);
      lcpCandidate.addEventListener('error', resolve);
    } else {
      resolve();
    }
  });
}

/**
 * If the block has a section class, moves the block to that section.
 * @param {Element} block - The block element to move
 */
function moveToSectionIfConfigured(block) {
  const sectionClass = [...block.classList].find((c) => c.startsWith('section-'));
  if (sectionClass && sectionClass !== 'section-metadata') {
    const sectionId = sectionClass.substring(8);
    const section = document.getElementById(sectionId).closest('.section');
    section.classList.add(`${block.dataset.blockName}-container`);
    section.append(block.parentElement);
  }
}

/**
 * Loads all blocks in a section.
 * @param {Element} section The section element
 */

async function loadSection(section, loadCallback) {
  const status = section.dataset.sectionStatus;
  if (!status || status === 'initialized') {
    section.dataset.sectionStatus = 'loading';
    const blocks = [...section.querySelectorAll('div.block')];
    for (let i = 0; i < blocks.length; i += 1) {
      moveToSectionIfConfigured(blocks[i]);
      await loadBlock(blocks[i]);
    }
    if (loadCallback) await loadCallback(section);
    section.dataset.sectionStatus = 'loaded';
    section.style.display = null;
  }
}

/**
 * Loads all sections.
 * @param {Element} element The parent element of sections to load
 */

async function loadSections(element) {
  const sections = [...element.querySelectorAll('div.section')];
  for (let i = 0; i < sections.length; i += 1) {
    if (sections[i].dataset.lazyload !== 'true') {
      await loadSection(sections[i]);
    }
    if (i === 0 && sampleRUM.enhance) {
      sampleRUM.enhance();
    }
  }
}

/**
 * Retrieves the value of a specified cookie.
 * @param {string} cookieStartName - The name of the cookie to retrieve, or how the cookie starts.
 * @returns {string} - The value of the cookie, or an empty string if the cookie is not found.
 */
function getCookie(cookieStartName) {
  const decodedCookie = decodeURIComponent(document.cookie);
  const ca = decodedCookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    const c = ca[i].trim();
    if (c.startsWith(cookieStartName)) {
      return c.substring(c.indexOf('=') + 1);
    }
  }
  return '';
}

// handling the UTM cookies
function getCUtmCookies() {
  const utmCookie = getCookie('utm');
  let utmCookieStorage = null;
  try {
    if (utmCookie) {
      utmCookieStorage = JSON.parse(decodeURI(utmCookie));
    }
  } catch (e) {
    console.warn(e);
  }
  return utmCookieStorage;
}

function setUtmCookie() {
  function setCCookie(key, stringData) {
    let cookieDomain = '; path=/;';
    if (!isDevHost()) {
      // we need to add domain only for production sites and not for preview deploys / staging sites
      cookieDomain = `;domain=celonis.com${cookieDomain}`;
    }
    // set UTMs in cooke storage,
    // we need to share stored UTMs with other subdomains such as 'free plan'
    document.cookie = `${key}=${stringData}${cookieDomain}`;
  }

  function setCookieFromQueryParam(queryparam) {
    const matchTypeCookieValue = new URLSearchParams(window.location.search).get(queryparam) || getCookie(queryparam) || '';
    setCCookie(queryparam, matchTypeCookieValue);
  }

  function getAIReferrer(referrer) {
    const items = [
      {
        domain: 'perplexity.ai',
        utmValue: 'perplexity',
      },
      {
        domain: 'chatgpt',
        utmValue: 'chatgpt',
      },
      {
        domain: '/x.ai',
        utmValue: 'grok',
      },
      {
        domain: 'gemini',
        utmValue: 'gemini',
      },
      {
        domain: 'phind',
        utmValue: 'phind',
      },
      {
        domain: '.andi.co',
        utmValue: 'andi',
      },
      {
        domain: '/you.com',
        utmValue: 'you_ai',
      },
      {
        domain: 'deepseek',
        utmValue: 'deepseek',
      },
      {
        domain: 'claude.ai',
        utmValue: 'claude',
      },
      {
        domain: 'ecosia',
        utmValue: 'ecosia',
      },
    ];

    return items.find((item) => referrer.includes(item.domain)) || '';
  }

  function setUtmsBasedOnReferralDomain() {
    const { referrer, location } = document;
    if (referrer.indexOf(location.hostname) === -1) {
      let utmSource = null;
      let utmMedium = null;
      const organicDomainsToCheck = ['google', 'yahoo', 'bing', 'duckduckgo', 'baidu'];
      const domainsToCheck = { 'event.eu.on24': 'on24' };
      const organicReferrerIndex = organicDomainsToCheck.findIndex((el) => referrer.includes(el));
      const aiReferrer = getAIReferrer(referrer);

      if (referrer.trim().length > 0) {
        if (aiReferrer) {
          utmSource = aiReferrer.utmValue;
          utmMedium = 'organic_seo';
        } else if (organicReferrerIndex > -1) {
          utmSource = organicDomainsToCheck[organicReferrerIndex];
          utmMedium = 'organic_seo';
        } else {
          const keysOfDomainsToCheck = Object.keys(domainsToCheck);
          const referrerIndex = keysOfDomainsToCheck.findIndex((el) => referrer.includes(el));
          utmSource = referrerIndex > -1 ? domainsToCheck[keysOfDomainsToCheck[referrerIndex]] : 'referral';
          utmMedium = 'referral';
        }
      } else {
        utmSource = 'direct';
        utmMedium = 'direct';
      }

      if (utmSource && utmMedium) {
        setCCookie(
          'utm',
          JSON.stringify({
            source: utmSource,
            medium: utmMedium,
            campaign: null,
            term: null,
            content: null,
          }),
        );
      }
    }
  }

  function setUtmCookieStorage() {
    const url = document.location.href;
    const queryString = url.substring(url.indexOf('?') + 1).split('&');
    const utmCookie = getCUtmCookies();
    let utmObject = {
      source: null,
      medium: null,
      campaign: null,
      term: null,
      content: null,
    };

    if (utmCookie) {
      utmObject = utmCookie;
    }

    let hasUtmAsQueryParams = false;
    for (let i = 0; i < queryString.length; i += 1) {
      queryString[i] = queryString[i].split('=');
      let utmKey = queryString[i][0];
      if (utmKey.startsWith('utm_')) {
        if (!hasUtmAsQueryParams) {
          utmObject = {
            source: null,
            medium: null,
            campaign: null,
            term: null,
            content: null,
          };
          hasUtmAsQueryParams = true;
        }
        utmKey = utmKey.replace('utm_', '');
        const utmValue = queryString[i][1] ? decodeURIComponent(queryString[i][1]) : null;
        utmObject[utmKey] = utmValue;
      }
    }

    if (hasUtmAsQueryParams) {
      setCCookie('utm', JSON.stringify(utmObject));
    } else if (
      (utmObject.medium === null && utmObject.source === null)
      || ['direct', 'organic_seo', 'referral'].includes(utmObject.medium)
    ) {
      // We can set new UTMs based on ReferralDomain only in cases when
      // existing cookies were set based on ReferralDomain.
      setUtmsBasedOnReferralDomain();
    }
  }

  setUtmCookieStorage();
  setCookieFromQueryParam('matchtype');
}

setUtmCookie();

function setGhSource() {
  const urlParams = new URL(window.location.href).searchParams;
  const ghSource = urlParams.get('gh_src');

  if (!ghSource) return;

  sessionStorage.setItem('ghSource', ghSource);
}

setGhSource();

function removeH6AndLeaveContent(main) {
  main?.querySelectorAll('h6').forEach((h6) => {
    const parent = h6.parentElement;
    if (parent?.tagName === 'DIV' && parent.children.length === 1) {
      h6.replaceWith(...h6.childNodes);
    } else {
      const p = document.createElement('p');
      p.innerHTML = h6.innerHTML;
      parent.replaceChild(p, h6);
    }
  });
}

init();

export {
  buildBlock,
  checkAssetExtension,
  createOptimizedPicture,
  createPictureFromLink,
  createLabels,
  decorateAssets,
  decorateBlock,
  decorateBlocks,
  decorateButtons,
  decorateIcons,
  decorateIconsToSVG,
  decorateIconButton,
  decorateSections,
  decorateTemplateAndTheme,
  fetchPlaceholders,
  getCookie,
  getMetadata,
  getPlaceholder,
  getTaxPlaceholder,
  loadBlock,
  loadCSS,
  loadFooter,
  loadHeader,
  loadScript,
  loadSection,
  loadSections,
  readBlockConfig,
  sampleRUM,
  setLazyVideosObserver,
  setup,
  toCamelCase,
  toClassName,
  waitForFirstImage,
  wrapTextNodes,
  isDevHost,
  getCUtmCookies,
  removeH6AndLeaveContent,
};
